/**
 * Model info in het fan-paneel (16-10-2026, Jordy): één bron, de persona-gids.
 *
 * 18-10-2026 (Jordy): geen ruwe gidssecties meer, maar nette balken die uit de
 * gids worden AFGELEID (``modelBalken``): Name, Age, Location, Work, Hobbies,
 * Personality, Language, Boundaries, plus Background, Standard answers en
 * Do not use. Bronvermeldingen ("(chats 6×)", "[verzonnen]", …) en opmaak
 * vallen weg; de inhoud blijft zoals hij in de gids staat (Nederlands, niet
 * vertaald). De beheersecties (Te bevestigen, Twijfels, Samenvoegkeuzes, Bron
 * Model Info) komen nooit in een balk.
 *
 * Opslaan:
 *   - een gidssectie   PATCH /models/personas/{id}/persona-gids/sectie {kop, tekst}
 *                      (alleen owner/admin, via het potlood bij een balk; de
 *                      server vervangt alleen die sectie)
 *   - Do's / Don'ts    PATCH /models/personas/{id}/profile {model_info: {dos|donts}}
 *   - notities         PATCH .../profile {notes}
 *   - eigen velden     PATCH .../profile {model_info: {_custom_fields: [...]}}
 * Chatters lezen de balken en bewerken Do's/Don'ts, notities en eigen velden
 * van hun eigen modellen.
 *
 * Geen React: de tests voeren dit uit met node (esbuild).
 */
import { alsTekst, eigenVelden } from './fanInfo'
import type { Info, Opslag, Regel } from './fanInfo'

/** Engelse labels voor de vaste koppen van de gids (kleine letters als sleutel). */
export const KOP_LABEL: Record<string, string> = {
  'naam': 'Name',
  'leeftijd en geboortedatum': 'Age',
  'woonplaats': 'Location',
  'beroep': 'Work',
  "hobby's en interesses": 'Hobbies',
  'persoonlijkheid en toon': 'Personality',
  'taal': 'Language',
  'grenzen': 'Boundaries',
  'achtergrond': 'Background',
  'vaste antwoorden': 'Standard answers',
  'niet meer gebruiken': 'Do not use',
}

/** Volgorde van de balken; deze acht staan er altijd (leeg = "—"). */
const VAST = ['naam', 'leeftijd en geboortedatum', 'woonplaats', 'beroep', "hobby's en interesses",
              'persoonlijkheid en toon', 'taal', 'grenzen']
/** Extra balken, alleen als de gids die sectie heeft. */
const EXTRA = ['achtergrond', 'vaste antwoorden', 'niet meer gebruiken']
/** Beheersecties: nooit in een balk (ook niet voor owner/admin). */
const BEHEER = ['bron model info (ruw)', 'te bevestigen door model', 'twijfels en keuzes (voor jordy)',
                'samenvoegkeuzes (voor jordy)']

/** Kop zonder hoofdletters en zonder toevoeging tussen haakjes. */
const kern = (kop: string) => kop.trim().toLowerCase().replace(/\s*\([^)]*\)\s*$/, '')

export function kopLabel(kop: string): string {
  return KOP_LABEL[kop.trim().toLowerCase()] ?? kop.trim()
}

export interface GidsSectie { kop: string; label: string; inhoud: string }

/** De ``##``-secties van de gids, in volgorde; ``###`` hoort bij de sectie erboven. */
export function gidsSecties(tekst: string | null | undefined): GidsSectie[] {
  const uit: GidsSectie[] = []
  let huidig: { kop: string; regels: string[] } | null = null
  const sluit = () => {
    if (huidig) uit.push({ kop: huidig.kop, label: kopLabel(huidig.kop),
                           inhoud: huidig.regels.join('\n').trim() })
  }
  for (const regel of String(tekst ?? '').split('\n')) {
    const m = regel.match(/^(#{1,2})\s+(.*?)\s*$/)
    if (m) {
      sluit()
      huidig = m[1] === '##' ? { kop: m[2], regels: [] } : null
      continue
    }
    if (huidig) huidig.regels.push(regel)
  }
  sluit()
  return uit
}

const profiel = (pid: number | string) => `/api/admin/models/personas/${pid}/profile`

export function sectieOpslag(pid: number | string, kop: string, tekst: string): Opslag {
  return { pad: `/api/admin/models/personas/${pid}/persona-gids/sectie`, body: { kop, tekst } }
}

export interface BalkItem { tekst: string; kop?: boolean }
export interface Balk {
  sleutel: string
  label: string
  /** De kop in de gids (om die sectie te bewerken); null = sectie ontbreekt. */
  kop: string | null
  /** De ruwe sectie, voor het potlood van owner/admin. */
  rauw: string
  items: BalkItem[]
}

// Bronvermeldingen tussen haakjes (ook met haakjes erin) en [verzonnen]/[keuze]:
// geen inhoud voor de chatter.
const BRON = /×|chats|Model Info|Mass DM|feitenblad|besluit|afgeleid|opdracht|posttitels|captions|stap \d|persona_yaml|gemarkeerd|bureau|stijl/
const VERZONNEN = /\s*\[(?:verzonnen|keuze)[^\]]*\]/g

function zonderBron(t: string): string {
  let uit = '', groep = '', diepte = 0
  for (const ch of t) {
    if (ch === '(') { diepte++; groep += ch; continue }
    if (diepte) {
      groep += ch
      if (ch === ')' && --diepte === 0) {
        if (BRON.test(groep)) uit = uit.replace(/\s+$/, '')
        else uit += groep
        groep = ''
      }
      continue
    }
    uit += ch
  }
  return uit + groep
}

/** Eén gidsregel zonder bron, opmaak en opsommingsteken. */
const OPMAAK: [RegExp, string][] = [
  [VERZONNEN, ''],
  [/\*\*|\x60/g, ''],   // vet en code-opmaak (\x60 = backtick)
  [/^\s*(?:[-*]|\d+[.)])\s+/, ''],
  [/\s{2,}/g, ' '],
  [/^[;,\s]+|[;,\s]+$/g, ''],
]

export function schoonRegel(regel: string): string {
  return OPMAAK.reduce((t, [re, door]) => t.replace(re, door), zonderBron(regel))
}

function items(inhoud: string): BalkItem[] {
  const uit: BalkItem[] = []
  for (const regel of inhoud.split('\n')) {
    if (!regel.trim()) continue
    const sub = regel.match(/^#{3,}\s+(.*)$/)
    const vetKop = /^\s*\*\*[^*]+:\*\*\s*$/.test(regel)
    const t = schoonRegel(sub ? sub[1] : regel)
    if (!t) continue
    uit.push(sub || vetKop ? { tekst: t.replace(/:$/, ''), kop: true } : { tekst: t })
  }
  return uit
}

/** Na "Label: waarde" alleen de waarde. */
const waardeNa = (t: string) => (t.includes(':') ? t.slice(t.indexOf(':') + 1).trim() : t)

function nettere(sleutel: string, lijst: BalkItem[]): BalkItem[] {
  if (sleutel === 'naam') {
    // "Schermnaam: Nina", "Roepnaam: Nina" -> "Nina".
    const namen = [...new Set(lijst.filter((i) => !i.kop).map((i) => waardeNa(i.tekst)).filter(Boolean))]
    return namen.map((tekst) => ({ tekst }))
  }
  if (sleutel === 'leeftijd en geboortedatum') {
    const alles = lijst.map((i) => i.tekst).join('\n')
    const leeftijd = alles.match(/leeftijd:\s*(\d{2})/i)?.[1]
    const geboren = alles.match(/geboortedatum:\s*(\d{1,2}-\d{1,2}-\d{4})/i)?.[1]
    if (leeftijd) return [{ tekst: leeftijd }, ...(geboren ? [{ tekst: `born ${geboren}` }] : [])]
    return lijst.filter((i) => !/^21\b/.test(i.tekst))
  }
  return lijst
}

/** De balken voor Model info, afgeleid uit de gids (zie de kop van dit bestand). */
export function modelBalken(tekst: string | null | undefined): Balk[] {
  const secties = gidsSecties(tekst)
  const vind = (sleutel: string) => secties.find((s) => kern(s.kop) === kern(sleutel))
  const balk = (sleutel: string): Balk => {
    const s = vind(sleutel)
    return { sleutel, label: KOP_LABEL[sleutel] ?? sleutel, kop: s?.kop ?? null, rauw: s?.inhoud ?? '',
             items: s ? nettere(sleutel, items(s.inhoud)) : [] }
  }
  const bekend = new Set([...VAST, ...EXTRA, ...BEHEER].map(kern))
  return [
    ...VAST.map(balk),
    ...EXTRA.filter((k) => vind(k)).map(balk),
    // Een kop die we niet kennen (en geen beheersectie): als balk met de kop zelf.
    ...secties.filter((s) => !bekend.has(kern(s.kop)))
      .map((s): Balk => ({ sleutel: kern(s.kop), label: s.kop.trim(), kop: s.kop, rauw: s.inhoud,
                           items: items(s.inhoud) })),
  ]
}

/**
 * 18-10-2026 (Jordy): de ENGELSE weergave van Model info (alleen het scherm; de
 * gids blijft Nederlands). Server: GET /models/personas/{id}/model-info-engels,
 * vertaald (Mistral) en gecachet per model + gidsversie
 * (src/services/model_info_engels.py, dezelfde afleiding als modelBalken).
 */
export interface ModelInfoEngels {
  klaar: boolean
  bron?: string
  balken?: Record<string, BalkItem[]>
  dos?: string[]
  donts?: string[]
}

/** De Engelse items van een balk als die er zijn en precies passen, anders het origineel. */
export function metEngels(b: Balk, e: ModelInfoEngels | null | undefined): BalkItem[] {
  const en = e?.klaar ? e.balken?.[b.sleutel] : undefined
  return en && en.length === b.items.length ? en : b.items
}

/** Do's / Don'ts als losse regels (zoals regels_van in model_info_engels.py). */
export function regelsVan(tekst: unknown): string[] {
  return String(tekst ?? '').split('\n')
    .map((r) => r.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim())
    .filter(Boolean)
}

/** De regels van Do's of Don'ts in het Engels als die precies passen, anders het origineel. */
export function regelsEngels(tekst: unknown, en: string[] | undefined, klaar: boolean | undefined): string[] {
  const nl = regelsVan(tekst)
  return klaar && en && en.length === nl.length ? en : nl
}

/**
 * 18-10-2026 (Jordy): het ANTWOORD uit een Standard answers-regel, om in het
 * berichtvak te zetten. "Vraag? — antwoord" -> het antwoord; staan er twee
 * varianten ("EN: "…" / NL: "…""), dan die van de gevraagde taal. Aanhalingstekens weg.
 */
export function antwoordUit(regel: string, taal: 'en' | 'nl'): string {
  const n = regel.indexOf(' — ')
  let a = n >= 0 ? regel.slice(n + 3) : regel
  const en = a.match(/\bEN:\s*(.+?)(?:\s*\/\s*NL:|$)/)
  const nl = a.match(/\bNL:\s*(.+?)(?:\s*\/\s*EN:|$)/)
  if (taal === 'en' && en) a = en[1]
  else if (taal === 'nl' && nl) a = nl[1]
  else if (en) a = en[1]
  return a.trim().replace(/^["“”']+|["“”']+$/g, '').trim()
}

/** Do's / Don'ts uit model_info (die staan niet in de gids). */
export function doDont(info: Info | null | undefined): { dos: string; donts: string } {
  const i = info ?? {}
  return { dos: alsTekst(i.dos), donts: alsTekst(i.donts) }
}

export function doDontOpslag(pid: number | string, sleutel: 'dos' | 'donts', waarde: string): Opslag {
  return { pad: profiel(pid), body: { model_info: { [sleutel]: waarde } } }
}

/**
 * 19-10-2026 (Jordy): twee INTERNE velden per model, `plaats` en `land`. Ze staan
 * in model_info, maar komen nooit in de context van fan-antwoorden (de server
 * sluit ze uit, src/services/persona_ai.py PRIVE_SLEUTELS). Een fan hoort alleen
 * "ergens in Nederland". Land is standaard "Nederland".
 */
export const LAND_STANDAARD = 'Nederland'

export function plaatsLand(info: Info | null | undefined): { plaats: string; land: string } {
  const mi = (info ?? {}) as Record<string, unknown>
  return {
    plaats: String(mi.plaats ?? '').trim(),
    land: String(mi.land ?? '').trim() || LAND_STANDAARD,
  }
}

export function plaatsLandOpslag(pid: number | string, sleutel: 'plaats' | 'land', waarde: string): Opslag {
  return { pad: profiel(pid), body: { model_info: { [sleutel]: waarde.trim() } } }
}

/** Notities en eigen velden van het model, als regels (zelfde vorm als Fan info). */
export function modelRegels(info: Info | null | undefined, notities: string | null | undefined): Regel[] {
  return [
    { id: 'notes', soort: 'notes', sleutel: 'notes', label: 'Model notes',
      waarde: String(notities ?? ''), meerregelig: true },
    ...eigenVelden(info).map((v, n): Regel => ({ id: `eigen:${n}`, soort: 'eigen', sleutel: String(n),
                                                  label: v.label || 'Custom field', waarde: v.value,
                                                  meerregelig: false })),
  ]
}

export function modelOpslag(pid: number | string, info: Info | null | undefined,
                            r: Regel, waarde: string): Opslag {
  if (r.soort === 'notes') return { pad: profiel(pid), body: { notes: waarde } }
  const lijst = eigenVelden(info)
  const n = Number(r.sleutel)
  if (lijst[n]) lijst[n] = { ...lijst[n], value: waarde }
  return { pad: profiel(pid), body: { model_info: { _custom_fields: lijst } } }
}

export function modelNieuwVeld(pid: number | string, info: Info | null | undefined,
                               label: string, waarde: string): Opslag | null {
  const l = label.trim()
  if (!l) return null
  return { pad: profiel(pid), body: { model_info: { _custom_fields: [...eigenVelden(info), { label: l, value: waarde }] } } }
}

export function modelVeldWeg(pid: number | string, info: Info | null | undefined, index: number): Opslag {
  return { pad: profiel(pid), body: { model_info: { _custom_fields: eigenVelden(info).filter((_, n) => n !== index) } } }
}
